// src/components/inventory/CorrectionSheet.js
//
// #682 and #702, Session 79.22b.6c.2 -- ONE SHEET FOR EVERY KIND OF MISTAKE
// (callout B). Owen signed off 100 frames and made 40; he loaded 40 on a truck
// that carried 4; Dallas counted in 4 when 40 came off. Correct on the step's
// line in the transfer's history opens this: the right number, a reason in one
// tap, an optional note, what will happen, Save. The drawings are frames P2, P7,
// S1 to S4, T1 and T2 of the corrections gate:
//   docs/gates/Stede_Corrections_Gate_682_702_761.html
//
// BUILT EXACTLY AS DRAWN (682 D14). Every size, colour and word below is the
// frame's own: the 26px number, the 40px reason pills, the 48px commits on a
// phone and 40px in the desk dialog, the inset "what will happen" line, the red
// refusal under the box. A bottom sheet on a phone and a tablet, a 440px centred
// dialog on a desk -- the same fields, words and order in both (callout O, F4).
//
// THE RIGHT NUMBER, NEVER THE DIFFERENCE (callout C): the database takes the
// whole record back and writes this number fresh; Stede works out what moves.
// The sheet opens holding the current number, selected, with Wrong number
// already chosen (682 D17), so the usual correction is: type, Save.
//
// EVERY PRESS IS ANSWERED (#492): a refusal from the database shows its own
// sentence in place, the typed number stays, and nothing is written; a save that
// never reached the database says so (S4).

import React, { useEffect, useRef, useState } from 'react';
import Overlay from '../Overlay';
import { useTier, tierFor, tierAtLeast } from '../../lib/tiers';
import {
  CORRECTION_REASONS, CORRECTION_WORDS, qtyText, cleanTyped, parseTyped, checkCorrection, refusalField,
  willHappenSignOff, willHappenLoad, willHappenCount, answerSignOff, answerLoad, answerCount,
  correctSignOff, correctLoad, correctCountIn,
  // #761, T5 -- a run for the shelf, from the item's Made line; and the bells.
  willHappenShelfRun, answerShelfRunCorrection, correctRecordProduction, notifyCorrection,
} from '../../lib/corrections';

// The frames' own tokens (gk-p / gk-s).
const INK = '#0F0F0F';
const INK2 = '#5C5C5C';
const INK3 = '#6A6A6A';
const EDGE = '#D0D0D0';
const FIELD = '#8A8A8A';
const INSET = '#F7F7F7';
const GREEN = '#39D353';
const BLOCK_BG = '#FFEBEE';
const BLOCK_INK = '#A32D2D';
const HEAD = "'Barlow Condensed', sans-serif";
const BODY = "'Barlow', sans-serif";

const headText = { fontFamily: HEAD, fontWeight: 700, letterSpacing: '.5px', textTransform: 'uppercase' };
const askStyle = { ...headText, margin: '10px 0 6px', fontSize: 12, color: INK2 };
const stopStyle = {
  margin: '8px 0 0', background: BLOCK_BG, color: BLOCK_INK, borderRadius: 8,
  padding: '8px 12px', fontSize: 13, lineHeight: 1.35,
};

const NO_EDGE = { borderTop: 'none', borderLeft: 'none', borderRight: 'none', borderBottom: 'none' };

const KIND_TITLE = { sign_off: 'Signed off', load: 'Loaded', count: 'Counted in', shelf_run: 'Made' };

// "9:42 am" as the frames write it, with the day when it was not today.
export function shortWhen(d, now = new Date()) {
  if (!(d instanceof Date) || Number.isNaN(d.getTime())) return null;
  const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
    .replace(/\s?([AP])M$/i, (m, p) => ` ${p.toLowerCase()}m`);
  const sameDay = d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth() && d.getDate() === now.getDate();
  return sameDay ? time : `${d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}, ${time}`;
}

// THE SUB-LINE (F2): what it is, then who, where and when. A sign-off with
// goods already on a truck says so (S1), because that is what the cap reads.
export function correctionSubLine({ kind, item, context, line = null, branchName }) {
  let first = item?.name || '';
  if (kind === 'load') first = `${item?.name || ''} on ${context?.loadRef || ''}`;
  if (kind === 'count') first = `${item?.name || ''} from ${context?.loadRef || ''}`;
  // #761, T5 -- "Stake Push 24 · for our own shelf".
  if (kind === 'shelf_run') first = `${item?.name || ''} · for our own shelf`;
  if (kind === 'sign_off' && line && Number(line.sent) > 0) {
    first = `${item?.name || ''} · ${qtyText(line.made)} made · ${qtyText(line.sent)} on a truck`;
  }
  const second = [item?.recordedByName || null, branchName || null, shortWhen(item?.at)].filter(Boolean).join(' · ');
  return [first, second];
}

// `transfer` is the transfer the step belongs to, for the bells' "other branch"
// (null for a run for the shelf, which belongs to none).
export default function CorrectionSheet({
  kind, context, items: choices = [], lineFacts = null, branchNames = {}, transferItemName = null,
  transfer = null, onClose, onSaved, onRaced,
}) {
  const { width, coarse } = useTier();
  const desk = tierAtLeast(tierFor(width), 'desk');
  const [item, setItem] = useState(choices.length === 1 ? choices[0] : null);
  const [typed, setTyped] = useState(choices.length === 1 ? qtyText(choices[0].qty) : '');
  const [reason, setReason] = useState('wrong_number');
  const [note, setNote] = useState('');
  const [problem, setProblem] = useState(null);   // { field, text }
  const [saving, setSaving] = useState(false);
  // What the line said when Save was pressed. The page reads the transfer again
  // while the sheet still shows Saving…, and a line worked out from the fresh
  // numbers would describe a second correction nobody asked for.
  const [willAtSave, setWillAtSave] = useState(null);
  const numberRef = useRef(null);

  // Opens holding the current number, SELECTED, so typing replaces it (F3).
  useEffect(() => {
    if (item && numberRef.current) {
      numberRef.current.focus();
      numberRef.current.select();
    }
  }, [item]);

  const branchId = context?.branchId;
  const branchName = branchNames[String(branchId)] || 'This branch';
  const line = kind === 'sign_off' && item ? (lineFacts?.(item) || null) : null;

  function choose(it) {
    if (it.blocked) return;
    setItem(it);
    setTyped(qtyText(it.qty));
    setProblem(null);
  }

  // WHAT WILL HAPPEN, before the press; hidden while ANY refusal shows, because
  // it would be false for a press that will not save (callout P). S3 and S4 draw
  // no inset line under their refusals either - Grok's comparison, 79.22b.6c.3.
  let will = null;
  const typedQty = parseTyped(typed);
  if (item && typedQty != null && !Number.isNaN(typedQty) && !problem) {
    if (kind === 'sign_off') {
      will = willHappenSignOff({ branch: branchName, was: item.qty, now: typedQty,
        lineMade: line?.made ?? item.qty, asked: line?.asked ?? 0 });
    } else if (kind === 'load') {
      will = willHappenLoad({ branch: branchName, dest: context?.dest || 'The other branch', was: item.qty, now: typedQty });
    } else if (kind === 'count') {
      will = willHappenCount({ branch: branchName, sender: context?.sender || 'The sending branch',
        was: item.qty, now: typedQty, loaded: item.loaded, askable: context?.askable !== false });
    } else if (kind === 'shelf_run') {
      will = willHappenShelfRun({ branch: branchName, was: item.qty, now: typedQty, onHand: context?.onHand ?? null });
    }
  }

  async function save() {
    if (saving || !item) return;
    const check = checkCorrection({ typed, current: item.qty, reason, note });
    if (check.field) { setProblem(check); return; }
    setProblem(null);
    setWillAtSave(will);
    setSaving(true);
    let out;
    if (kind === 'sign_off') {
      out = await correctSignOff({ productionId: item.id, qty: check.qty, reason, note });
    } else if (kind === 'load') {
      out = await correctLoad({ loadItemId: item.id, qty: check.qty, reason, note, was: item.qty });
    } else if (kind === 'shelf_run') {
      out = await correctRecordProduction({ txId: item.id, qty: check.qty, reason, note });
    } else {
      out = await correctCountIn({
        loadItemId: item.id, qty: check.qty, reason, note, was: item.qty,
        branchId, itemId: item.itemId, unitCost: item.unitCost, loaded: item.loaded,
      });
    }
    if (out.refusal) {
      setSaving(false);
      setProblem({ field: refusalField(out.refusal), text: out.refusal });
      if (out.race) onRaced?.();
      return;
    }
    if (out.failure || !out.result) {
      setSaving(false);
      setProblem({ field: 'other', text: out.failure || CORRECTION_WORDS.failed });
      return;
    }
    const r = out.result;
    const name = item.name || transferItemName || '';
    const answer = kind === 'sign_off'
      ? answerSignOff({ now: r.now, item: name, made: r.made, asked: line?.asked ?? 0, ready: r.ready, sent: r.sent })
      : kind === 'load'
        ? answerLoad({ loadRef: r.load_ref || context?.loadRef, now: r.now, item: name, dest: context?.dest || 'The other branch' })
        : kind === 'shelf_run'
          ? answerShelfRunCorrection({ now: r.now, item: name, branch: branchName, onHand: r.qty_on_hand })
          : answerCount({ now: r.now, item: name, loadRef: r.load_ref || context?.loadRef, asked: r.asked, sender: context?.sender || 'The sending branch' });
    // THE BELLS (gate B5), once the database has named the correction: the one
    // save path for all four kinds sends them, so no screen can forget.
    await notifyCorrection({
      kind, result: r, transfer, itemName: name, asked: line?.asked ?? null, branchNames,
      itemNames: item.itemId != null ? { [String(item.itemId)]: name } : {},
    });
    // The transfer is read again before the answer shows (gate B3); the sheet
    // stays on Saving… until then, and closes when the page has the fresh rows.
    await onSaved?.(answer, r);
  }

  const commitH = desk ? (coarse ? 48 : 40) : 48;
  const btn = {
    // P7's desk buttons are padded 10px a side, the phone's 12 (Grok's comparison).
    margin: 0, height: commitH, borderRadius: 8, padding: desk ? '0 10px' : '0 12px', ...headText, fontSize: 14,
    display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1,
    background: '#fff', color: INK, border: `1px solid ${EDGE}`, flex: 1, cursor: 'pointer', whiteSpace: 'nowrap',
  };
  const primary = { ...btn, background: GREEN, borderColor: GREEN, color: INK };
  const field = (bad) => ({
    width: '100%', height: 48, boxSizing: 'border-box', border: `1px solid ${bad ? BLOCK_INK : FIELD}`, borderRadius: 8,
    padding: '0 12px', background: '#fff', color: INK, ...headText, textTransform: 'none', fontSize: 26,
  });
  const closeSize = desk ? 32 : 40;

  // Titled with what was recorded (callout B): the item's number once one is
  // chosen; before that, the whole truck's or count's, with its truck beneath.
  const [sub1, sub2] = item
    ? correctionSubLine({ kind, item, context, line, branchName })
    : [`${kind === 'count' ? 'from' : 'on'} ${context?.loadRef || ''}`, ''];
  const wholeQty = (Array.isArray(context?.items) ? context.items : choices)
    .reduce((n, it) => n + (Number(it.qty) || 0), 0);
  const title = `${KIND_TITLE[kind]} ${qtyText(item ? item.qty : wholeQty)}`;

  return (
    <Overlay
      variant={desk ? 'modal' : 'sheet'}
      onClose={saving ? undefined : onClose}
      maxWidth={440}
      zIndex={3100}
      // The frames draw the sheet and the dialog with no edge line; the shared
      // pop-up's 0.5px #ddd is cleared for this one (Grok's comparison, 79.22b.6c.3).
      panelStyle={desk
        ? { borderRadius: 12, padding: 16, boxShadow: '0 4px 12px rgba(0,0,0,.1)', boxSizing: 'border-box', ...NO_EDGE }
        : { padding: '8px 16px 16px', boxShadow: '0 4px 12px rgba(0,0,0,.1)', boxSizing: 'border-box', ...NO_EDGE }}
    >
      <div role="dialog" aria-modal="true" aria-label={`Correct ${title}`} style={{ fontFamily: BODY, color: INK }}>
        {!desk && <div style={{ width: 36, height: 4, borderRadius: 999, background: EDGE, margin: '0 auto 8px' }} />}
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
          <div>
            <p style={{ ...headText, margin: 0, fontSize: 12, color: INK3 }}>Correct</p>
            <h3 style={{ ...headText, margin: 0, fontSize: 20, lineHeight: 1.1 }}>{title}</h3>
            {(item || context?.loadRef) && (
              <p style={{ margin: '4px 0 0', fontSize: 13, color: INK2, lineHeight: 1.35 }}>
                {desk ? [sub1, sub2].filter(Boolean).join(' · ') : <>{sub1}{sub2 && <><br />{sub2}</>}</>}
              </p>
            )}
          </div>
          <button
            type="button" aria-label="Close" onClick={saving ? undefined : onClose}
            style={{ width: closeSize, height: closeSize, borderRadius: 8, border: `1px solid ${EDGE}`, background: '#fff',
              color: INK, fontSize: 20, lineHeight: 1, flex: 'none', display: 'flex', alignItems: 'center',
              justifyContent: 'center', cursor: 'pointer', padding: 0 }}
          >×</button>
        </div>

        {!item ? (
          // A TRUCK OR COUNT WITH SEVERAL ITEMS lists them first, each corrected
          // on its own (X2, F2 NOT DRAWN): one 48px row per item.
          <div>
            <p style={askStyle}>Which item?</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {choices.map(it => (
                <div key={it.id}>
                  <button
                    type="button" onClick={() => choose(it)} aria-disabled={it.blocked ? true : undefined}
                    style={{ ...btn, width: '100%', height: 48, justifyContent: 'flex-start', textTransform: 'none',
                      fontFamily: BODY, fontWeight: 600, fontSize: 14, letterSpacing: 0 }}
                  >
                    {`${it.name} · ${qtyText(it.qty)} ${kind === 'count' ? 'counted' : 'loaded'}`}
                  </button>
                  {it.blocked && <p style={{ ...stopStyle, background: '#FFF8EC', color: '#8A5A08' }}>{it.blocked}</p>}
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
              <button type="button" onClick={onClose} style={btn}>Cancel</button>
            </div>
          </div>
        ) : (
          <div>
            <p style={askStyle}><label htmlFor="correction-number">The right number</label></p>
            <input
              id="correction-number" ref={numberRef} type="text" inputMode="decimal" autoComplete="off"
              value={typed} aria-invalid={problem?.field === 'number' || undefined}
              aria-describedby={problem?.field === 'number' ? 'correction-problem' : undefined}
              onChange={e => { setTyped(cleanTyped(e.target.value)); if (problem?.field === 'number') setProblem(null); }}
              style={field(problem?.field === 'number')}
            />
            {problem?.field === 'number' && <p id="correction-problem" role="alert" style={stopStyle}>{problem.text}</p>}

            <p style={askStyle} id="correction-reason">Reason</p>
            <div role="radiogroup" aria-labelledby="correction-reason" style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {CORRECTION_REASONS.map(r => {
                const on = reason === r.value;
                return (
                  <button
                    key={r.value} type="button" role="radio" aria-checked={on}
                    onClick={() => { setReason(r.value); if (problem?.field === 'note') setProblem(null); }}
                    style={{ minHeight: 40, padding: '0 14px', borderRadius: 999, border: `1px solid ${on ? GREEN : EDGE}`,
                      background: on ? GREEN : '#fff', color: INK, ...headText, fontSize: 14, lineHeight: 1,
                      display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}
                  >{r.label}</button>
                );
              })}
            </div>

            <p style={askStyle}><label htmlFor="correction-note">Note</label></p>
            <input
              id="correction-note" type="text" value={note}
              // The frames' placeholder grey, #6A6A6A (index.css, .correction-note).
              className="correction-note"
              placeholder={reason === 'other' ? 'Say what happened' : 'Add a note (optional)'}
              aria-invalid={problem?.field === 'note' || undefined}
              onChange={e => { setNote(e.target.value); if (problem?.field === 'note') setProblem(null); }}
              style={{ ...field(problem?.field === 'note'), fontFamily: BODY, fontWeight: 400, fontSize: 16, letterSpacing: 0 }}
            />
            {problem?.field === 'note' && <p role="alert" style={stopStyle}>{problem.text}</p>}

            {(saving ? willAtSave : will) && (
              <p style={{ margin: '12px 0 0', background: INSET, borderRadius: 8, padding: '8px 12px', fontSize: 13, color: INK, lineHeight: 1.35 }}>
                {saving ? willAtSave : will}
              </p>
            )}
            {problem?.field === 'other' && <p role="alert" style={stopStyle}>{problem.text}</p>}

            <div style={{ display: 'flex', gap: 8, alignItems: 'stretch', marginTop: 12 }}>
              <button type="button" onClick={save} disabled={saving} style={{ ...primary, cursor: saving ? 'not-allowed' : 'pointer' }}>
                {saving ? 'Saving…' : 'Save correction'}
              </button>
              <button type="button" onClick={saving ? undefined : onClose} disabled={saving} style={btn}>Cancel</button>
            </div>
          </div>
        )}
      </div>
    </Overlay>
  );
}
